/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import GeneralModule from '@/store/modules/General';

type AlertColor = 'danger' | 'error' | 'success' | 'warning' | 'info' | 'primary' | 'secondary' | '';

export type ExtAxiosConfig = InternalAxiosRequestConfig & {
  __silentLoading?: boolean;
  __loadingKey?: string;
  __silentAlert?: boolean;
  __successToast?: boolean;
  __requestStartedAt?: number;
};

type AlertQueuedPayload = {
  id: string;
  alertMsg: string;
  alertColor: AlertColor;
};

const GeneralInstance: any = getModule(GeneralModule, store);

// -------------------- LOADING --------------------
let pendingLoading = 0;

const loadingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const loadingVisibleKeys = new Set<string>();

// -------------------- ALERT STATE --------------------
let queuedAlertPayload: AlertQueuedPayload | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

const isSilentLoading = (config?: ExtAxiosConfig) => {
  if (!config) return false;
  if (config.__silentLoading) return true;

  const h: any = (config.headers as any) || {};
  return Boolean(h['x-silent-loading'] || h['X-SILENT-LOADING']);
};

export const shouldHandleLoading = (config?: ExtAxiosConfig) => {
  if (!config) return false;
  return !isSilentLoading(config);
};

export const attachLoadingKey = (config?: ExtAxiosConfig) => {
  if (!config) return;
  if (config.__loadingKey) return;
  config.__loadingKey = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const genId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const showToast = (payload: AlertQueuedPayload) => {
  if (typeof GeneralInstance?.ShowAlert !== 'function') return;

  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }

  toastTimer = setTimeout(() => {
    toastTimer = null;

    GeneralInstance.ShowAlert({
      id: payload.id,
      alertMsg: payload.alertMsg,
      alertColor: payload.alertColor,
      isAlert: true
    });
  }, 0);
};

function flushQueuedAlert() {
  if (pendingLoading !== 0) return;
  if (!queuedAlertPayload) return;

  const payload = queuedAlertPayload;
  queuedAlertPayload = null;

  setTimeout(() => {
    showToast(payload);
  }, 0);
}

const showGlobalLoading = () => {
  pendingLoading += 1;
  if (pendingLoading === 1) {
    GeneralInstance?.toggleLoading?.(true);
  }
};

const hideGlobalLoading = () => {
  pendingLoading = Math.max(0, pendingLoading - 1);
  if (pendingLoading === 0) {
    GeneralInstance?.toggleLoading?.(false);
    flushQueuedAlert();
  }
};

export const scheduleLoading = (config?: ExtAxiosConfig, delayMs = 300) => {
  if (!config || !shouldHandleLoading(config)) return;

  attachLoadingKey(config);
  const key = config.__loadingKey;
  if (!key) return;

  if (loadingTimers.has(key) || loadingVisibleKeys.has(key)) return;

  const timer = setTimeout(() => {
    loadingTimers.delete(key);
    loadingVisibleKeys.add(key);
    showGlobalLoading();
  }, delayMs);

  loadingTimers.set(key, timer);
};

export const finishLoading = (config?: ExtAxiosConfig) => {
  if (!config || !shouldHandleLoading(config)) return;

  const key = config.__loadingKey;
  if (!key) return;

  const timer = loadingTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    loadingTimers.delete(key);
  }

  if (loadingVisibleKeys.has(key)) {
    loadingVisibleKeys.delete(key);
    hideGlobalLoading();
  }
};

// -------------------- ALERT --------------------
const isSilentAlert = (config?: ExtAxiosConfig) => {
  if (!config) return false;
  if (config.__silentAlert) return true;

  const h: any = (config.headers as any) || {};
  return Boolean(h['x-silent-alert'] || h['X-SILENT-ALERT']);
};

export const showAlert = (config: ExtAxiosConfig | undefined, opts: { msg: unknown; color?: AlertColor }) => {
  if (isSilentAlert(config)) return;

  const safeMsg = (opts?.msg || '').toString().trim();
  if (!safeMsg) return;

  const payload: AlertQueuedPayload = {
    id: genId(),
    alertMsg: safeMsg,
    alertColor: (opts.color || 'info') as AlertColor
  };

  if (pendingLoading > 0) {
    queuedAlertPayload = payload;
    return;
  }

  showToast(payload);
};

// -------------------- BUSINESS STATUS helpers ----------------
export const pickBusinessStatus = (data: any) => {
  const st = data?.status;
  const code = st?.code ?? data?.code ?? data?.statusCode ?? data?.errorCode ?? data?.errCode;
  const message = st?.message ?? data?.message ?? data?.msg ?? data?.errorMessage ?? data?.error?.message;

  const isOk = code === 0 || code === '0';
  return { isOk, code, message };
};

export const httpFallbackMsg = (status: number) => {
  if (status === 400) return 'Bad request. Please check your input.';
  if (status === 401) return 'Unauthorized. Please login again.';
  if (status === 403) return 'Permission denied.';
  if (status === 404) return 'Not found.';
  if (status === 408) return 'Request timeout. Please try again.';
  if (status === 429) return 'Too many requests. Please slow down.';
  if (status >= 500) return 'Server error. Please try again later.';
  return 'Something went wrong. Please try again.';
};

export const asExtCfg = (cfg: InternalAxiosRequestConfig | undefined) => cfg as ExtAxiosConfig | undefined;
export const asExtRespCfg = (resp: AxiosResponse) => resp?.config as ExtAxiosConfig | undefined;
